"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { ChartLine, Cpu, Target, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const competencies = [{
  title: "Sell-Side M&A",
  description: "Maximizing enterprise value through strategic positioning, rigorous buyer identification, and expert negotiation for $2M-$30M EBITDA businesses.",
  icon: ChartLine,
  href: "/AdvisoryServicesPage"
}, {
  title: "Capital Raises",
  description: "Securing growth capital and debt financing from institutional partners to fuel expansion, acquisitions, or shareholder liquidity events.",
  icon: Cpu,
  href: "/CapitalRaisesPage"
}, {
  title: "Investment Mandate",
  description: "Active deployment of capital into high-potential firms with $1M–$5M EBITDA, focusing on long-term legacy and operational excellence.",
  icon: Target,
  href: "/AdvisoryServicesPage"
}];
export default function OurCoreCompetencies() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222632" id="strategic-advisory-pillars" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--color-chart-2)_0%,_transparent_70%)] opacity-10 pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className={`max-w-3xl mb-16 md:mb-24 transition-all duration-1000 ease-out ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight text-primary mb-6">
            Strategic Advisory & <br className="hidden md:block" />
            <span className="text-chart-3">Capital Solutions</span>
          </h2>
          <p className="text-lg text-muted-foreground font-serif max-w-2xl leading-relaxed">
            Blue Phoenix Ventures provides institutional-grade advisory services tailored for middle-market business owners. We bridge the gap between local legacy and global capital markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {competencies.map((item, i) => {
          const Icon = item.icon;
          return <div key={item.title} data-index={i} className={`transition-all duration-700 ease-out delay-${i * 200} ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Card className="group h-full flex flex-col bg-card backdrop-blur-md border-chart-3/30 hover:border-chart-3 hover:bg-gradient-to-br hover:from-white hover:to-accent/30 transition-all duration-500 overflow-hidden relative rounded-sm shadow-sm hover:shadow-xl">
                  <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-chart-3 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardHeader className="pb-4 pt-8">
                    <div className="mb-6 p-3 bg-accent/50 rounded-sm w-fit text-chart-3 group-hover:scale-110 transition-transform duration-500 ease-out">
                      <Icon size={28} strokeWidth={1.5} />
                    </div>
                    <CardTitle className="font-default text-xl font-semibold tracking-tight text-primary group-hover:text-primary transition-colors duration-300">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="flex-grow pb-8">
                    <CardDescription className="font-serif text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                  
                  <CardFooter className="pt-0 pb-8 mt-auto">
                    <Link to={item.href} className="inline-flex items-center text-sm font-mono font-medium uppercase tracking-widest text-chart-3 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 focus-visible:opacity-100 focus-visible:translate-x-0 outline-none rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                      Learn More
                      <ChevronRight className="ml-1 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}